import { FC } from "react";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconBlock } from "@/dashboard-design-system/DashboardIconBlock/DashboardIconBlock";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Copy, ExternalLink, Heart, Organization } from "@/icons/outlined";

interface NonprofitSectionProps {
  organizationName: string;
  ownerEmail: string | undefined;
  onCopyEmail: () => void;
  formLink: string;
  formName: string;
}

export const NonprofitSection: FC<NonprofitSectionProps> = ({
  organizationName,
  ownerEmail,
  onCopyEmail,
  formLink,
  formName,
}) => {
  const { t } = useTranslate();

  return (
    <Stack gap={2}>
      <DashboardTypography variant="subtitle2" vibe="text-neutral-quiet" sx={{ textTransform: "uppercase" }}>
        {t("dashboard", "donorTransactionDetailDrawer.nonprofitDetails")}
      </DashboardTypography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" gap={2} alignItems="flex-start">
            <DashboardIconBlock vibe="neutral" size="medium" sx={{ mt: 0.25, flexShrink: 0 }}>
              <Organization />
            </DashboardIconBlock>
            <Stack sx={{ minWidth: 0 }}>
              <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                {t("dashboard", "donorTransactionDetailDrawer.nonprofit")}
              </DashboardTypography>
              <DashboardTypography
                variant="body2"
                vibe="text-neutral-moderate"
                sx={{ fontWeight: 600 }}
                data-sensitive="organization-name"
              >
                {organizationName}
              </DashboardTypography>
              {ownerEmail && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <DashboardTypography
                    variant="body2"
                    vibe="text-neutral-moderate"
                    sx={{ wordBreak: "break-all" }}
                    data-sensitive="email"
                  >
                    {ownerEmail}
                  </DashboardTypography>
                  <DashboardIcon
                    size="small"
                    vibe="text-neutral-quiet"
                    onClick={onCopyEmail}
                    sx={{ cursor: "pointer", flexShrink: 0 }}
                  >
                    <Copy />
                  </DashboardIcon>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack direction="row" gap={2} alignItems="flex-start">
            <DashboardIconBlock vibe="neutral" size="medium" sx={{ mt: 0.25, flexShrink: 0 }}>
              <Heart />
            </DashboardIconBlock>
            <Stack sx={{ minWidth: 0 }}>
              <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                {t("dashboard", "donorTransactionDetailDrawer.campaign")}
              </DashboardTypography>
              {formLink ? (
                <DashboardLink variant="body2" href={formLink} target="_blank" sx={{ wordBreak: "break-word" }}>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <span data-test="form-title-text">{formName}</span>
                    <DashboardIcon size="small" vibe="currentColor">
                      <ExternalLink />
                    </DashboardIcon>
                  </Stack>
                </DashboardLink>
              ) : (
                <DashboardTypography variant="body2" vibe="text-neutral-moderate" data-test="form-title-text">
                  {formName}
                </DashboardTypography>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
